import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import "./ManageSubscription.scss";
import dayjs from "dayjs";
import { Modal } from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ManageSubscriptionMenu from "./ManageSubscriptionMenu";
import ManagePropertySubscription from "./ManagePropertySubscription";
import ManageAgentSubscription from "./ManageAgentSubscription";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../redux/store";
import { CommonEnums } from "../../../enums/common.enums";
import { SubscriptionApi } from "../../../utils/api/SubscriptionApi";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { subscriptionAction } from "../../../redux/features/subscriptionSlice";
import { clientRoutes } from "../../../utils/routes";
import Loading from "../../loading/Loading";
import SearchIcon from "../../../components/icons/SearchIcon";
import ProtectedComponent from "../../../components/ProtectedComponent";
import NavigationLayout from "../navigation";
dayjs.extend(customParseFormat);

const stripePromise = loadStripe(
  "pk_test_51SLDDkKqGdfB2DqLCbwi0nn8yeYbM9sLRKKMvx767qNOxQF54GVXLa2NLY0qoKTG4cXv7wjqgN7MYfC2YGYxYTSM00qh69HPXt",
);

const ManageSubscription = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [propertySearch, setPropertySearch] = useState("");
  const [detectedCountry, setDetectedCountry] = useState<string>("");

  const [activeSubscriptions, setActiveSubscriptions] = useState<any[]>([]);
  const [isActiveSubLoading, setIsActiveSubLoading] = useState(true);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);

  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);

  const subscriptionListReset = useSelector(
    (state: RootState) => state?.subscription?.subscriptionListReset,
  );
  let subscriptionsTabId = useSelector(
    (state: RootState) => state?.subscription?.subscriptionsTab,
  );
  let listingTabId = useSelector(
    (state: RootState) => state?.subscription?.listingTab,
  );

  const [selectedFeatureId, setSelectedFeatureId] = useState("");
  const [state, setState] = useState<any>({
    isLoading: true,
    totalPage: 1,
    pageNumber: 1,
    listData: [],
  });

  const getSubscriptionsApi = async () => {
    setIsHistoryLoading(true);

    try {
      if (
        subscriptionsTabId === CommonEnums.subscriptionType.agent_marketing.key
      ) {
        const filters: any = {
          subscription_type: subscriptionsTabId,
          listing_type: listingTabId,
        };

        const res: any = await SubscriptionApi.getMyActiveSubscription(filters);

        if (res?.data?.success && res?.data?.data) {
          setActiveSubscriptions([res.data.data]);
        } else {
          setActiveSubscriptions([]);
        }

        setState((prev: any) => ({
          ...prev,
          totalPage: 1,
          pageNumber: 1,
        }));

        return;
      }

      const filters: any = {
        subscription_type: subscriptionsTabId,
        listing_type: listingTabId,
        page: pageNumber,
        limit: 20,
      };

      if (
        subscriptionsTabId ===
          CommonEnums.subscriptionType.property_marketing.key &&
        propertySearch.trim()
      ) {
        filters.search = propertySearch.trim();
      }

      const res: any = await SubscriptionApi.getMySubscriptionHistory(filters);

      if (res?.data?.success) {
        const data = res.data.data || [];
        setActiveSubscriptions(data);

        const pagination = res.data.pagination;
        setState((prev: any) => ({
          ...prev,
          totalPage: pagination.totalPage,
          pageNumber: pagination.currentPage,
        }));
      } else {
        setActiveSubscriptions([]);
      }
    } catch (err) {
      console.error(err);
      setActiveSubscriptions([]);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    getSubscriptionsApi();
  }, [subscriptionsTabId, listingTabId, pageNumber]);

  useEffect(() => {
    if (
      subscriptionsTabId === CommonEnums.subscriptionType.property_marketing.key
    ) {
      const delay = setTimeout(() => {
        setPageNumber(1);
        getSubscriptionsApi();
      }, 400);

      return () => clearTimeout(delay);
    }
  }, [propertySearch]);

  useEffect(() => {
    setSelectedSubscription(null);
  }, [subscriptionsTabId, listingTabId]);

  const getPricingDetails = (planData: any) => {
    if (!planData || !planData.stripe_product_prices) {
      return {
        price: planData?.price || 0,
        currency: "AUD",
        stripePriceId: "",
      };
    }

    const user: any = LocalStorageHelpers.getUserData();
    const storageCountry = user?.country || "";

    const finalCountryCode = detectedCountry || storageCountry || "AU";

    let targetCurrency = "aud";

    if (finalCountryCode === "IN") {
      targetCurrency = "inr";
    } else if (finalCountryCode === "US") {
      targetCurrency = "usd";
    } else if (finalCountryCode === "AU") {
      targetCurrency = "aud";
    }

    const prices = planData.stripe_product_prices;

    let priceObj = prices.find((p: any) => p.currency === targetCurrency);

    if (!priceObj) {
      priceObj = prices.find((p: any) => p.currency === "aud");
    }

    if (!priceObj && prices.length > 0) {
      priceObj = prices[0];
    }

    if (priceObj) {
      return {
        price: priceObj.unit_amount / 100,
        currency: priceObj.currency.toUpperCase(),
        stripePriceId: priceObj.id,
      };
    }

    return { price: 0, currency: "AUD", stripePriceId: "" };
  };

  const onClickSubscriptionTab = (id: string) => {
    dispatch(subscriptionAction?.setSubscriptionsTab(id));
  };
  const onClickListingTab = (id: string) => {
    dispatch(subscriptionAction?.setListingTab(id));
  };
  const onClicksetToggle = (id: string) => {
    setSelectedFeatureId(selectedFeatureId === id ? "" : id);
  };

  const getPlanSubtitle = (sub: any) => {
    if (
      sub.subscription_type ===
      CommonEnums.subscriptionType.property_marketing.key
    ) {
      let listingText = "";

      if (sub.for_sale) listingText = "For Sale";
      if (sub.for_rent) listingText = "For Rent";

      return `Property Marketing Fee One-off (${listingText})`;
    }

    return sub.subscription_type
      ?.split("_")
      .map((w: any) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const handleManageSubscription = (sub: any) => {
    let targetRoute = "";

    if (
      sub.subscription_type ===
      CommonEnums.subscriptionType.property_marketing.key
    ) {
      targetRoute = clientRoutes.propertyPlans;
    } else {
      targetRoute = clientRoutes.agentPlans;
    }

    navigate(`${targetRoute}?subscrpition_history_id=${sub._id}`, {
      state: { subscriptionData: sub },
    });
  };

  const handleExplorePlans = () => {
    navigate("/subscription");
  };

  // if (selectedSubscription) {
  //   if (
  //     selectedSubscription.subscription_type ===
  //     CommonEnums.subscriptionType.property_marketing.key
  //   ) {
  //     return (
  //       <ManagePropertySubscription
  //         data={selectedSubscription}
  //         onBack={() => setSelectedSubscription(null)}
  //       />
  //     );
  //   } else if (
  //     selectedSubscription.subscription_type ===
  //     CommonEnums.subscriptionType.agent_marketing.key
  //   ) {
  //     return (
  //       <ManageAgentSubscription
  //         data={selectedSubscription}
  //         onBack={() => setSelectedSubscription(null)}
  //       />
  //     );
  //   }
  // }

  const getPlanData = (sub: any) => {
    return sub.subscription_plan || sub.subscription_id || null;
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="subscription-list-container">
          <div className="subscriptions-cards-heading">
            <p>Manage Subscriptions</p>
          </div>

          <p className="ledger-sub-title font-inter-400">
            Manage your active subscriptions, upgrades, cancellations, and more
          </p>

          {/* <p className="tab-heading">Active plans</p> */}

          <div className="subscription-filter-tab-container">
            <div className="tab-heading-and-select-box-contain">
              <div className="subscriptions-for-tab-contain tabs-contain">
                {subscriptionTab.map((data) => (
                  <button
                    key={data.id}
                    className={`tab-btn ${
                      data.id === subscriptionsTabId ? "tab-button-active" : ""
                    }`}
                    onClick={() => onClickSubscriptionTab(data.id)}
                  >
                    {data.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="tab-heading-and-select-box-contain">
              <div className="subscriptions-for-tab-contain tabs-contain">
                {listingTab.map((data) => (
                  <button
                    key={data.id}
                    className={`tab-btn ${
                      data.id === listingTabId ? "tab-button-active" : ""
                    }`}
                    onClick={() => onClickListingTab(data.id)}
                  >
                    {data.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {subscriptionsTabId ===
            CommonEnums.subscriptionType.property_marketing.key && (
            <div className="property-search-box">
              <SearchIcon />
              <input
                type="text"
                placeholder="Search by Property"
                value={propertySearch}
                onChange={(e) => setPropertySearch(e.target.value)}
              />
            </div>
          )}

          <div className="current-subscription-section">
            {isHistoryLoading ? (
              <div className="list-page-loading">
                <Loading />
              </div>
            ) : activeSubscriptions.length > 0 ? (
              <div className="active-subscription-wrapper">
                {activeSubscriptions.map((sub: any) => {
                  const priceDetails = getPricingDetails(getPlanData(sub));

                  return (
                    <div className="active-subscription-card" key={sub._id}>
                      <div className="plan-header">
                        <div className="plan-details">
                          <p className="plan-title">
                            {getPlanData(sub)?.plan_name}
                          </p>
                          <p
                            className={
                              sub.subscription_type ===
                              CommonEnums.subscriptionType.property_marketing
                                .key
                                ? "plan-subtitle plan-subtitle-property"
                                : "plan-subtitle"
                            }
                          >
                            {getPlanSubtitle(sub)}
                          </p>
                          {sub.subscription_type ===
                            CommonEnums.subscriptionType.property_marketing
                              .key && (
                            <p className="plan-property-address font-inter-400">
                              {sub.property_address || "No property address"}
                            </p>
                          )}
                        </div>
                        <div className="plan-status">
                          <span className="status-dot"></span>
                          <span className="status-text">Active</span>
                          {/* <ManageSubscriptionMenu /> */}
                        </div>
                      </div>

                      <div className="plan-body">
                        <p className="plan-price">
                          {priceDetails.currency} {priceDetails.price}
                          {sub.subscription_type !==
                            CommonEnums.subscriptionType.property_marketing
                              .key && (
                            <span>
                              per <br /> month
                            </span>
                          )}
                        </p>
                      </div>

                      {sub.subscription_type !==
                        CommonEnums.subscriptionType.property_marketing.key && (
                        <div className="plan-footer">
                          <p className="plan-billing-info font-inter-400">
                            Next Payment:{" "}
                            {dayjs(
                              sub.subscription_end_date,
                              "DD-MM-YYYY",
                            ).isValid()
                              ? dayjs(
                                  sub.subscription_end_date,
                                  "DD-MM-YYYY",
                                ).format("MMMM DD, YYYY")
                              : "N/A"}
                          </p>
                        </div>
                      )}

                      <div
                        className="manage-sub-link font-inter-400"
                        onClick={() => handleManageSubscription(sub)}
                      >
                        Manage subscription
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-subscription-card">
                <p className="message-title font-inter-400">
                  You do not have any active subscriptions.
                </p>
                <p className="message-subtitle font-inter-400">
                  Purchase subscription plans to get started and unlock all
                  features.
                </p>
                <button
                  className="explore-plans-btn font-inter-400"
                  onClick={handleExplorePlans}
                >
                  {subscriptionsTabId ===
                  CommonEnums.subscriptionType.property_marketing.key
                    ? "Explore property marketing plans"
                    : "Explore agent marketing plans"}
                </button>
              </div>
            )}
          </div>

          {subscriptionsTabId ===
            CommonEnums.subscriptionType.property_marketing.key &&
            activeSubscriptions.length > 0 && (
              <div className="pagination-wrapper">
                <button
                  className="pg-btn"
                  disabled={pageNumber === 1}
                  onClick={() => setPageNumber(pageNumber - 1)}
                >
                  ‹
                </button>

                <span className="pg-info">
                  Page {pageNumber} of {state.totalPage}
                </span>

                <button
                  className="pg-btn"
                  disabled={pageNumber === state.totalPage}
                  onClick={() => setPageNumber(pageNumber + 1)}
                >
                  ›
                </button>
              </div>
            )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default ManageSubscription;

const subscriptionTab = [
  {
    id: CommonEnums.subscriptionType.property_marketing.key,
    title: CommonEnums.subscriptionType.property_marketing.value,
  },
  {
    id: CommonEnums.subscriptionType.agent_marketing.key,
    title: CommonEnums.subscriptionType.agent_marketing.value,
  },
];
const listingTab = [
  {
    id: CommonEnums.listingType.for_sale.key,
    title: CommonEnums.listingType.for_sale.value,
  },
  {
    id: CommonEnums.listingType.for_rent.key,
    title: CommonEnums.listingType.for_rent.value,
  },
];
